import { Alert, Box, Tab, Tabs } from '@mui/material';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

import BudgetGroupsTab from '@/components/features/UserDetails/BudgetGroupsTab';
import DistributorGroupsTab from '@/components/features/UserDetails/DistributorGroupsTab';
import Breadcrumb from '@/components/shared/Breadcrumb';
import { SectionTitle } from '@/components/shared/Typography/Typography';
import { useUserById } from '@/hooks/useUserById';
import { ROUTES } from '@/routes';

import AllocationGroupsTab from './AllocationGroupsTab';
import TabPanel from './components/TabPanel';
import UserInformationTab from './UserInformationTab';

function a11yProps(index: number) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  };
}

function UserDetails() {
  const { id } = useParams<{ id: string }>();
  const { user, isLoading, error } = useUserById(id);
  const [searchParams, setSearchParams] = useSearchParams();

  const getTab = (params: URLSearchParams) => {
    const tab = parseInt(params.get('tab') || '0', 10);
    return isNaN(tab) || tab < 0 || tab > 3 ? 0 : tab;
  };

  const currentTab = getTab(searchParams);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    const params = new URLSearchParams(searchParams);

    params.delete('page');
    params.delete('pageSize');

    if (newValue === 0) params.delete('tab');
    else params.set('tab', newValue.toString());

    setSearchParams(params, { replace: true, state: location.state });
  };

  const location = useLocation();
  const getBreadcrumbPath = () => {
    const state = location.state as { returnSearchParams?: string } | undefined;
    const returnSearchParams = state?.returnSearchParams;

    if (returnSearchParams) {
      return `${ROUTES.USERS}?${returnSearchParams}`;
    }

    return ROUTES.USERS;
  };

  const breadcrumbItems = [
    { label: 'User Dashboard', path: getBreadcrumbPath() },
    {
      label: 'User Information',
      path: `/users/${id}`,
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Breadcrumb items={breadcrumbItems} />
      <SectionTitle data-testid='user-details-title'>User Information - {user?.email || ''}</SectionTitle>
      <Box sx={{ display: 'flex', gap: '32px', width: '100%' }}>
        <Tabs
          orientation='vertical'
          variant='scrollable'
          value={currentTab}
          onChange={handleChange}
          aria-label='User details tabs'
          data-testid='user-details-tabs'
          sx={{
            minWidth: 200,
            '& .MuiTabs-indicator': {
              left: 0,
              right: 'auto',
              backgroundColor: '#000000',
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              textAlign: 'left',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              color: '#000000',
              minHeight: '40px',
              padding: '12px',
              fontSize: '14px',
              fontWeight: 400,
            },
            '& .MuiTab-root.Mui-selected': {
              fontWeight: 700,
              color: '#000000',
            },
          }}
        >
          <Tab label='User Information' {...a11yProps(0)} data-testid='tab-user-information' />
          <Tab label='Inventory Allocations' {...a11yProps(1)} data-testid='tab-inventory-allocation-groups' />
          <Tab label='Distributor Groups' {...a11yProps(2)} data-testid='tab-distributor-groups' />
          <Tab label='Budget Groups' {...a11yProps(3)} data-testid='tab-budget-groups' />
        </Tabs>
        <Box sx={{ flexGrow: 1 }}>
          <TabPanel value={currentTab} index={0}>
            {isLoading && <div data-testid='user-info-loading'>Loading...</div>}
            {error && (
              <Alert severity='error' sx={{ display: 'flex', alignItems: 'center', fontSize: '14px', lineHeight: '20px' }}>
                Error: {error.message}
              </Alert>
            )}
            <UserInformationTab user={user ?? undefined} />
          </TabPanel>
          <TabPanel value={currentTab} index={1}>
            {isLoading && <div data-testid='allocation-groups-loading'>Loading...</div>}
            {error && (
              <Alert severity='error' sx={{ display: 'flex', alignItems: 'center', fontSize: '14px', lineHeight: '20px' }}>
                Error: {error.message}
              </Alert>
            )}
            <AllocationGroupsTab userId={id} email={user?.email || undefined} />
          </TabPanel>
          <TabPanel value={currentTab} index={2}>
            <DistributorGroupsTab userId={id} email={user?.email || undefined} />
          </TabPanel>
          <TabPanel value={currentTab} index={3}>
            <BudgetGroupsTab userId={id} email={user?.email || undefined} />
          </TabPanel>
        </Box>
      </Box>
    </Box>
  );
}

export default UserDetails;
